<template>
	<div>
		<TransitionRoot
			as="template"
			:show="sidebarOpen">
			<Dialog
				as="div"
				class="relative z-40 lg:hidden"
				@close="sidebarOpen = false">
				<TransitionChild
					as="template"
					enter="transition-opacity ease-linear duration-300"
					enter-from="opacity-0"
					enter-to="opacity-100"
					leave="transition-opacity ease-linear duration-300"
					leave-from="opacity-100"
					leave-to="opacity-0">
					<div class="fixed inset-0 bg-gray-900/80" />
				</TransitionChild>
  
				<div class="fixed inset-0 flex">
					<TransitionChild
						as="template"
						enter="transition ease-in-out duration-300 transform"
						enter-from="-translate-x-full"
						enter-to="translate-x-0"
						leave="transition ease-in-out duration-300 transform"
						leave-from="translate-x-0"
						leave-to="-translate-x-full">
						<DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1 dark:bg-gray-900">
							<TransitionChild
								as="template"
								enter="ease-in-out duration-300"
								enter-from="opacity-0"
								enter-to="opacity-100"
								leave="ease-in-out duration-300"
								leave-from="opacity-100"
								leave-to="opacity-0">
								<div class="absolute left-full top-0 flex w-16 justify-center pt-5">
									<button
										type="button"
										class="-m-2.5 p-2.5"
										@click="sidebarOpen = false">
										<span class="sr-only">Close sidebar</span>
										<XMarkIcon
											class="h-6 w-6 text-white"
											aria-hidden="true" />
									</button>
								</div>
							</TransitionChild>
							<!-- Sidebar component, swap this element with another sidebar if you like -->
							<div class="flex grow flex-col gap-y-5 overflow-y-auto bg-dbBlue px-6 pb-4 dark:bg-black/10 dark:ring-1 dark:ring-white/10">
								<div class="flex h-16 shrink-0 items-center">
									<img
										:class="[brand.logos.sidebar.class, 'left-0']"
										:src="brand.logos.sidebar.src"
										:alt="brand.logos.sidebar.alt">
								</div>
								<nav class="flex flex-1 flex-col">
									<ul
										role="list"
										class="flex flex-1 flex-col gap-y-7">
										<li>
											<ul
												role="list"
												class="-mx-2 space-y-1">
												<li
													v-for="item in navigation"
													:key="item.name">
													<router-link
														:to="item.to"
														:class="[$route.meta.module === item.module ? 'bg-indigo-900 text-white dark:bg-gray-800' : 'text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800', 'group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold']">
														<component
															:is="item.icon"
															:class="[$route.meta.module === item.module ? 'text-white' : 'text-indigo-200 group-hover:text-white dark:text-gray-400', 'h-6 w-6 shrink-0']"
															aria-hidden="true" />
														{{ item.name }}
													</router-link>
												</li>
											</ul>
										</li>
										<li class="mt-auto">
											<span class="text-xs font-semibold uppercase tracking-wide text-indigo-300 dark:text-gray-500">Additional Resources</span>
											<ul
												role="list"
												class="-mx-2 mt-2 space-y-1">
												<li>
													<a
														:href="brand.blogUrl"
														target="_blank"
														rel="noopener noreferrer"
														class="group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800">
														<NewspaperIcon
															class="h-5 w-5 shrink-0 text-indigo-200 group-hover:text-white dark:text-gray-400"
															aria-hidden="true" />
														Read Our Blog
													</a>
												</li>
												<li>
													<button
														type="button"
														@click="openWebinarModal()"
														class="group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800">
														<PlayCircleIcon
															class="h-5 w-5 shrink-0 text-indigo-200 group-hover:text-white dark:text-gray-400"
															aria-hidden="true" />
														View Our Latest Webinar
													</button>
												</li>
												<li>
													<button
														type="button"
														@click="openNewsletterModal()"
														class="group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800">
														<EnvelopeIcon
															class="h-5 w-5 shrink-0 text-indigo-200 group-hover:text-white dark:text-gray-400"
															aria-hidden="true" />
														Subscribe to Our Newsletter
													</button>
												</li>
											</ul>
										</li>
									</ul>
								</nav>
							</div>
						</DialogPanel>
					</TransitionChild>
				</div>
			</Dialog>
		</TransitionRoot>
  
		<!-- Static sidebar for desktop -->
		<div :class="[systemStore.system.sidebarExpanded ? 'lg:w-72' : 'lg:w-[72px]', 'hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:flex-col dark:bg-gray-900 transition-all duration-200 ease-in-out']">
			<!-- Sidebar component, swap this element with another sidebar if you like -->
			<div class="flex grow flex-col gap-y-5 bg-dbBlue px-6 pb-4 dark:bg-black/10 dark:ring-1 dark:ring-white/5 group/sidebar relative">
				<div class="flex h-16 shrink-0 items-center">
					<img
						v-show="systemStore.system.sidebarExpanded"
						:class="[brand.logos.sidebar.class, 'left-0 mt-3']"
						:src="brand.logos.sidebar.src"
						:alt="brand.logos.sidebar.alt">
					<img
						v-show="!systemStore.system.sidebarExpanded"
						:class="[brand.logos.sidebarCollapsed.class, 'left-0 mt-2']"
						:src="brand.logos.sidebarCollapsed.src"
						:alt="brand.logos.sidebarCollapsed.alt">
				</div>
				<button
					@click="collapseSidebar()"
					class="shadow-md border border-gray-200 bg-white text-dbBlue hover:bg-dbBlue hover:text-white p-1 opacity-0 rounded-full group-hover/sidebar:opacity-100 transition-all duration-50 ease-in-out absolute right-0 -mr-[14px] top-5 cursor-pointer dark:bg-gray-900 dark:text-gray-100 dark:border-gray-800 dark:hover:bg-indigo-500">
					<ChevronLeftIconMini 
						:class="[{'rotate-180': !systemStore.system.sidebarExpanded}, 'pr-[2px] w-5 h-5 transition-all ease-in-out duration-200']"
						aria-hidden="true" />
				</button>
				<nav class="flex flex-1 flex-col">
					<ul
						role="list"
						class="flex flex-1 flex-col gap-y-7">
						<li>
							<ul
								role="list"
								class="-mx-2 space-y-1">
								<li
									v-for="item in navigation"
									:key="item.name">
									<router-link
										:to="item.to"
										:class="[$route.meta.module === item.module ? 'bg-indigo-900 text-white dark:bg-gray-800' : 'text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800', 'group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold']">
										<component
											:is="item.icon"
											:class="[$route.meta.module === item.module ? 'text-white' : 'text-indigo-200 group-hover:text-white dark:text-gray-400', 'h-6 w-6 shrink-0']"
											aria-hidden="true" />
										<span v-show="systemStore.system.sidebarExpanded">{{ item.name }}</span>
									</router-link>
								</li>
							</ul>
						</li>
						<li class="mt-auto">
							<span
								v-show="systemStore.system.sidebarExpanded"
								class="text-xs font-semibold uppercase tracking-wide text-indigo-300 dark:text-gray-500">Additional Resources</span>
							<ul
								role="list"
								class="-mx-2 mt-2 space-y-1">
								<li>
									<a
										:href="brand.blogUrl"
										target="_blank"
										rel="noopener noreferrer"
										class="group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800">
										<NewspaperIcon
											class="h-5 w-5 shrink-0 text-indigo-200 group-hover:text-white dark:text-gray-400"
											aria-hidden="true" />
										<span v-show="systemStore.system.sidebarExpanded">Read Our Blog</span>
									</a>
								</li>
								<li>
									<button
										type="button"
										@click="openWebinarModal()"
										class="group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800">
										<PlayCircleIcon
											class="h-5 w-5 shrink-0 text-indigo-200 group-hover:text-white dark:text-gray-400"
											aria-hidden="true" />
										<span v-show="systemStore.system.sidebarExpanded">View Our Latest Webinar</span>
									</button>
								</li>
								<li>
									<button
										type="button"
										@click="openNewsletterModal()"
										class="group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 text-indigo-200 hover:text-white hover:bg-indigo-900 dark:text-gray-400 dark:hover:bg-gray-800">
										<EnvelopeIcon
											class="h-5 w-5 shrink-0 text-indigo-200 group-hover:text-white dark:text-gray-400"
											aria-hidden="true" />
										<span v-show="systemStore.system.sidebarExpanded">Subscribe to Our Newsletter</span>
									</button>
								</li>
							</ul>
						</li>
					</ul>
				</nav>
			</div>
		</div>

		<div :class="[systemStore.system.sidebarExpanded ? 'lg:pl-72' : 'lg:pl-[72px]', 'transition-all duration-200 ease-in-out']">
			<div class="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8 dark:border-white/5 dark:bg-gray-900">
				<button
					type="button"
					class="-m-2.5 p-2.5 text-gray-700 lg:hidden"
					@click="sidebarOpen = true">
					<span class="sr-only">Open sidebar</span>
					<Bars3Icon
						class="h-6 w-6"
						aria-hidden="true" />
				</button>
  
				<!-- Separator -->
				<div
					class="h-6 w-px bg-gray-900/10 lg:hidden dark:bg-white/5"
					aria-hidden="true" />
  
				<div class="flex flex-1 gap-x-4 self-stretch justify-end lg:gap-x-6">
					<div class="flex items-center gap-x-4 lg:gap-x-6">  
						<!-- Account dropdown -->
						<Menu
							as="div"
							class="relative">
							<MenuButton class="-m-1.5 flex items-center p-1.5">
								<span class="sr-only">Open user menu</span>
								<div
									class="h-8 w-8 rounded-full bg-gray-300 flex justify-center items-center">
									<UserIcon class="h-5 w-5" />
								</div>
								<span class="hidden lg:flex lg:items-center">
									<span
										class="ml-4 text-sm font-semibold leading-6 text-gray-900 dark:text-white"
										aria-hidden="true">{{ displayName }}</span>
									<ChevronDownIcon
										class="ml-2 h-5 w-5 text-gray-400"
										aria-hidden="true" />
								</span>
							</MenuButton>
							<transition
								enter-active-class="transition ease-out duration-100"
								enter-from-class="transform opacity-0 scale-95"
								enter-to-class="transform opacity-100 scale-100"
								leave-active-class="transition ease-in duration-75"
								leave-from-class="transform opacity-100 scale-100"
								leave-to-class="transform opacity-0 scale-95">
								<MenuItems class="absolute right-0 z-10 mt-2.5 w-40 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none dark:bg-gray-900 dark:ring-white/5">
									<MenuItem
										v-for="item in userNavigation"
										:key="item.name"
										v-slot="{ active }">
										<router-link
											:to="item.to"
											:class="[active ? 'bg-gray-50 dark:bg-gray-800' : '', 'block px-3 py-1 text-sm leading-6 text-gray-900 dark:text-white']">
											{{ item.name }}
										</router-link>
									</MenuItem>
								</MenuItems>
							</transition>
						</Menu>
					</div>
				</div>
			</div>
  
			<main class="py-5 bg-gray-50 dark:bg-gray-900 min-h-screen">
				<div class="px-4 sm:px-6 lg:px-8">
					<!-- Your content -->
					<slot />
				</div>
			</main>
		</div>

		<WebinarModal ref="webinarModal" />
		<NewsletterModal ref="newsletterModal" />
	</div>
</template>

<script lang="ts">
	export default { name: 'SidebarWithHeader' } // name the component
</script>

<script setup lang="ts">
	// components
	import {
		Dialog,
		DialogPanel,
		Menu,
		MenuButton,
		MenuItem,
		MenuItems,
		TransitionChild,
		TransitionRoot,
	} from '@headlessui/vue'
	import WebinarModal from '@/components/Modals/WebinarModal.vue'
	import NewsletterModal from '@/components/Modals/NewsletterModal.vue'

	// icons
	import {
		Bars3Icon,
		EnvelopeIcon,
		NewspaperIcon,
		PlayCircleIcon,
		XMarkIcon,
	} from '@heroicons/vue/24/outline'
	import {
		ChevronDownIcon,
		UserIcon,
		ChevronLeftIcon as ChevronLeftIconMini
	} from '@heroicons/vue/20/solid'

	// store
	import { 
		store as _systemStore
	} from '@/store/system'

	import {
		store as _userStore
	} from '@/store/user'

	// utilities
	import {
		showsLicensing
	} from '@/utilities/typeOfWork'

	import {
		fetchLicenses
	} from '@/modules/Licensing/queries'

	import {
		useBrand
	} from '@/utilities/brand'

	// vue
	import {
		computed,
		onMounted,
		ref
	} from 'vue'

	const systemStore = _systemStore()
	const userStore = _userStore()
	const brand = useBrand()

	// load file paths of all modules named Nav.vue
	const context = require.context('@/modules', true, /Nav.ts$/i)

	// import each component
	const modules = context.keys()
		.map(context)
		.map((m: any) => m.nav) 

	const typeOfWork = userStore?.user?.cognitoUser?.attributes?.["custom:typeOfWork"]

	// only looked up for customers without FM, who would otherwise never see the menu item
	const hasLicenses = ref(false)

	const showLicensing = computed(() => showsLicensing(typeOfWork, hasLicenses.value))

	// sort them based on the position data prop. computed, so Licensing appears once the license
	// lookup below comes back
	const navigation = computed(() => modules.filter((m) => {
		if(!showLicensing.value && m.name === 'Licensing') {
			return false
		}

		return m.show
	}).sort((a, b) => {
		let aPos = a.position
		let bPos = b.position
		if(aPos < bPos) {
			return -1
		}
		else if (aPos > bPos) {
			return 1
		}
		else {
			return 0
		}
	}))

	// customers with FM already see Licensing, so only the rest pay for a FileMaker lookup. a failed
	// lookup leaves the menu item hidden, as it was before the lookup existed
	onMounted(async () => {
		if (showLicensing.value) return

		try {
			hasLicenses.value = (await fetchLicenses()).length > 0
		}
		catch (e) {
			console.error(e)
		}
	})

	const userNavigation = [
		{ name: 'My Profile', to: {name: 'MyProfile'} },
		{ name: 'Log out', to: {name: 'SignOut'} },
	]
  
	const sidebarOpen = ref(false)

	const webinarModal = ref<{open: () => void} | null>(null)
	const newsletterModal = ref<{open: () => void} | null>(null)

	// open the latest webinar modal from the sidebar's Additional Resources section
	function openWebinarModal() {
		webinarModal.value?.open()
	}

	// open the newsletter signup modal from the sidebar's Additional Resources section
	function openNewsletterModal() {
		newsletterModal.value?.open()
	}

	const displayName = computed(() => {
		return [userStore?.user?.cognitoUser?.attributes?.given_name, userStore?.user?.cognitoUser?.attributes?.family_name].filter(Boolean).join(" ")
	})

	function collapseSidebar() {
		systemStore.system.sidebarExpanded = !systemStore.system.sidebarExpanded
	}
  </script>
  